import { HttpClient } from '@angular/common/http';
import { DestroyRef, effect, inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

interface ApiResponse<T> {
  status: number;
  message: string;
  DATA: T;
}

@Injectable({
  providedIn: 'root'
})
export class ChatService implements OnDestroy {
  private readonly apiUrl = environment.apiUrl;
  private unreadMessagesSubject = new BehaviorSubject<number>(0);
  public unreadMessages$ = this.unreadMessagesSubject.asObservable();

  // Socket.io properties
  private socket: Socket | null = null;
  private destroy$ = new Subject<void>();

  // New message observable
  private newMessageSubject = new BehaviorSubject<any>(null);
  public newMessage$ = this.newMessageSubject.asObservable();

  // Typing indicator observable
  private typingIndicatorSubject = new BehaviorSubject<{ userId: string, conversationId: string, isTyping: boolean } | null>(null);
  public typingIndicator$ = this.typingIndicatorSubject.asObservable();

  private destroyRef = inject(DestroyRef);

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private toastService: ToastService,
    private router: Router
  ) {
    effect(() => {
      const isAuthenticated = this.authService.authState();
      if (isAuthenticated) {
        this.connectSocket();
        this.loadUnreadMessagesCount();
      } else {
        this.disconnectSocket();
        this.unreadMessagesSubject.next(0);
      }
    });
  }

  /**
   * Updates the seenChat timestamp for the current user
   * @returns Observable with update response
   */
  public updateSeenChat(): Observable<ApiResponse<any>> {
    const currentUser = this.authService.getCurrentUser();

    if (!currentUser || !currentUser._id) {
      throw new Error('User not authenticated');
    }

    return this.http.put<ApiResponse<any>>(`${this.apiUrl}/user/${currentUser._id}/seenChat`, {}).pipe(
      tap((res) => {
        currentUser!.seenChat = new Date(res.DATA.seenChat);
        this.authService.setCurrentUser(currentUser!);
        this.unreadMessagesSubject.next(0);
      })
    );
  }

  public updateMessageCount(count: number) {
    this.unreadMessagesSubject.next(count);

  }

  /**
   * Gets the count of unread messages for the current user
   * @returns Observable with unread count response
   */
  public getUnreadMessageCount(): Observable<ApiResponse<{ unreadCount: number }>> {
    const currentUser = this.authService.getCurrentUser();

    if (!currentUser || !currentUser._id) {
      throw new Error('User not authenticated');
    }

    return this.http.get<ApiResponse<{ unreadCount: number }>>(`${this.apiUrl}/user/${currentUser._id}/unreadMessages`).pipe(
      tap(response => {
        if (response?.DATA?.unreadCount !== undefined) {
          this.unreadMessagesSubject.next(response.DATA.unreadCount);
        }
      })
    );
  }

  /**
   * Loads the unread messages count from the API
   */
  public loadUnreadMessagesCount(): void {

    if (this.authService.isLoggedIn()) {
      this.getUnreadMessageCount().subscribe({
        next: () => { }, // Count is updated via the tap operator
        error: () => {
          this.toastService.showError('Beim Laden der ungelesenen Nachrichten ist ein Fehler aufgetreten');
        }
      });
    }

  }

  /**
   * Initialize socket connection and set up event listeners
   */
  private initializeSocket(): void {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser || !currentUser._id) return;

    // Initialize socket connection
    const socketUrl = this.apiUrl.replace('/api', '');

    this.socket = io(socketUrl, {
      transports: ['websocket'],
      path: '/socket.io'
    });

    // Authenticate with socket server
    const accessToken = this.authService.getAccessToken();
    if (accessToken && accessToken.trim() !== '') {
      this.socket.emit('authenticate', {
        userId: currentUser._id,
        token: accessToken
      });
    }

    // Listen for new messages
    this.socket.on('new_message', (data) => {
      this.newMessageSubject.next(data);
    });

    // Listen for unread count updates
    this.socket.on('unread_count', (data) => {
      // The message count should not be updated if the user is on the chat page
      if (this.router.url === '/chat') {
        return;
      }

      if (data && typeof data.count === 'number') {
        this.unreadMessagesSubject.next(data.count);
      }
    });

    // Listen for typing indicators
    this.socket.on('typing_indicator', (data) => {
      this.typingIndicatorSubject.next(data);
    });

    // Handle successful connection
    this.socket.on('connect', () => {
      // Load unread messages count when socket connects
      this.loadUnreadMessagesCount();
    });

    // Handle reconnection
    this.socket.on('reconnect', () => {
      // Re-authenticate after reconnection with fresh token
      const freshUser = this.authService.getCurrentUser();
      const freshToken = this.authService.getAccessToken();

      if (freshUser && freshUser._id && freshToken && freshToken.trim() !== '') {
        this.socket?.emit('authenticate', {
          userId: freshUser._id,
          token: freshToken
        });
        // Reload unread messages after successful authentication setup
        this.loadUnreadMessagesCount();
      } else {
        // No valid user or token - disconnect socket
        this.socket?.disconnect();
      }
    });

    // Handle connection errors
    this.socket.on('connect_error', (error) => {
      Sentry.captureException(error, {
        tags: {
          component: 'ChatService',
          action: 'socket_connect_error'
        },
        extra: {
          message: (error as Error)?.message,
          name: (error as Error)?.name,
          description: (error as any)?.description,
          context: (error as any)?.context,
          socketUrl,
          transport: this.socket?.io?.opts?.transports,
          path: this.socket?.io?.opts?.path
        }
      });
      this.toastService.showError('Beim Verbinden mit dem Socket ist ein Fehler aufgetreten');
    });

    // Handle authentication errors
    this.socket.on('authentication_error', (error) => {
      Sentry.captureException(error, {
        tags: {
          component: 'ChatService',
          action: 'socket_authentication_error'
        }
      });
    });

  }

  /**
   * Connect or reconnect socket if user is authenticated
   */
  public connectSocket(): void {
    if (!this.authService.isLoggedIn()) return;

    if (this.socket && !this.socket.connected) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    if (!this.socket) {
      this.initializeSocket();
    }
  }

  /**
   * Disconnects the socket
   */
  private disconnectSocket(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Sends a message via Socket.io
   * @param conversationId The ID of the conversation
   * @param message The message text to send
   * @returns Promise that resolves when message is acknowledged
   */
  public sendMessageViaSocket(conversationId: string, message: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const currentUser = this.authService.getCurrentUser();
      if (!this.socket || !currentUser || !currentUser._id) {
        reject(new Error('Socket not connected or user not authenticated'));
        return;
      }

      // Send the message via socket
      this.socket.emit('send_message', {
        conversationId,
        message,
        authorId: currentUser._id
      });

      // Listen for confirmation that message was sent
      this.socket.once('message_sent', (response) => {
        resolve(response);
      });

      // Listen for errors
      this.socket.once('message_error', (error) => {
        reject(error);
      });

      // Timeout after 5 seconds
      setTimeout(() => {
        reject(new Error('Message sending timed out'));
      }, 5000);
    });
  }

  /**
   * Sends a typing indicator via Socket.io
   * @param conversationId The ID of the conversation
   * @param isTyping Whether the user is typing or not
   */
  public sendTypingIndicator(conversationId: string, isTyping: boolean): void {
    const currentUser = this.authService.getCurrentUser();
    if (!this.socket || !currentUser || !currentUser._id) return;

    this.socket.emit('typing', {
      conversationId,
      userId: currentUser._id,
      isTyping
    });
  }

  /**
   * Cleanup on service destruction
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnectSocket();
  }
}
